/**
 * Per-frame render orchestration. Owns the depth texture, the HDR scene texture,
 * the camera uniform buffer, the camera bind group, the day/night module, and the
 * shadow pass. Subsystem render passes (sky, terrain, buildings, shadow, post) are
 * coordinated here.
 *
 * Bind-group convention shared with all consumers:
 *   group(0) = camera
 *   group(1) = pass-private (e.g. terrain UBO + heightmap, buildings UBO + instance buf)
 *   group(2) = daynight
 *   group(3) = shadow (depth + comparison sampler + sun viewProj)
 *
 * Camera UBO layout (160 bytes):
 *   viewProj:    mat4x4<f32>    bytes   0..63
 *   eye:         vec4<f32>      bytes  64..79  (xyz = eye, w = 1)
 *   viewport:    vec4<f32>      bytes  80..95  (xy = px size, z = aspect, w = time seconds)
 *   invViewProj: mat4x4<f32>    bytes  96..159
 *
 * Render order per frame:
 *   1. shadow.draw(encoder, sunDir)        — depth-only pass into shadow.depth
 *   2. main render pass into HDR target    — sky → terrain → buildings
 *   3. post render pass into swapchain     — tonemap + bloom + vignette
 */

import { config } from '../config.ts';
import { formats } from '../gpu/formats.ts';
import { createUniformBuffer, writeUniform } from '../gpu/buffers.ts';
import type { GpuContext } from '../gpu/device.ts';
import type { Mat4 } from '../util/mat.ts';
import { mat4Invert } from '../util/mat.ts';
import type { Vec3 } from '../util/vec.ts';
import { createTerrainPass, type TerrainPass } from './terrain.ts';
import { createTerrainGeometry, type TerrainGeometry } from './terrain-geometry.ts';
import { createSkyPass, type SkyPass } from './sky.ts';
import { createBuildingsPass, type BuildingsPass } from './buildings.ts';
import { createTreesPass, type TreesPass } from './trees.ts';
import { createCarsPass, type CarsPass } from './cars.ts';
import { createShadowPass, type ShadowPass } from './shadow.ts';
import { createPostPass, type PostPass } from './post.ts';
import type { GenArtifacts } from '../gen/orchestrator.ts';
import type { CarsModule } from '../cars/cars.ts';
import { createDayNight, type DayNightModule } from '../time/daynight.ts';

export const CAMERA_UBO_SIZE = 160;

export interface FrameRenderer {
  render(camera: { viewProj: Mat4; eye: Vec3 }, timeSec: number): void;
  resize(): void;
  destroy(): void;
  readonly daynight: DayNightModule;
}

export function createFrameRenderer(gpu: GpuContext, gen: GenArtifacts, cars: CarsModule): FrameRenderer {
  const { device, context, presentationFormat } = gpu;

  const cameraBuffer = createUniformBuffer(device, CAMERA_UBO_SIZE, 'camera');
  const cameraBindGroupLayout = device.createBindGroupLayout({
    label: 'camera.bgl',
    entries: [{
      binding: 0,
      visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
      buffer: { type: 'uniform', minBindingSize: CAMERA_UBO_SIZE },
    }],
  });
  const cameraBindGroup = device.createBindGroup({
    label: 'camera.bg',
    layout: cameraBindGroupLayout,
    entries: [{ binding: 0, resource: { buffer: cameraBuffer } }],
  });

  const daynight = createDayNight(device);

  const terrainGeometry: TerrainGeometry = createTerrainGeometry(device);

  const shadow: ShadowPass = createShadowPass(device, {
    depthFormat: formats.depth,
    shadowMapSize: config.shadowMapSize,
    heightmapView: gen.heightmap.view,
    terrainGeometry,
    instanceBuffer: gen.buildings.instanceBuffer,
    instanceCountBuffer: gen.buildings.countBuffer,
    maxInstances: gen.buildings.maxInstances,
    treeInstanceBuffer: gen.trees.instanceBuffer,
    treeInstanceCountBuffer: gen.trees.countBuffer,
    maxTreeInstances: gen.trees.maxInstances,
    carInstanceBuffer: cars.instanceBuffer,
    carInstanceCount: cars.maxInstances,
  });

  // HDR scene target — sky/terrain/buildings render into this; post tonemaps to swapchain.
  let depthTexture: GPUTexture | null = null;
  let depthView: GPUTextureView | null = null;
  let hdrTexture: GPUTexture | null = null;
  let hdrView: GPUTextureView | null = null;
  const recreateTargets = (): void => {
    depthTexture?.destroy();
    hdrTexture?.destroy();
    depthTexture = device.createTexture({
      label: 'frame.depth',
      size: { width: gpu.canvas.width, height: gpu.canvas.height },
      format: formats.depth,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });
    depthView = depthTexture.createView();
    hdrTexture = device.createTexture({
      label: 'frame.hdr',
      size: { width: gpu.canvas.width, height: gpu.canvas.height },
      format: formats.hdrColor,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    hdrView = hdrTexture.createView();
  };
  recreateTargets();

  const sky: SkyPass = createSkyPass(device, {
    presentationFormat: formats.hdrColor,    // sky writes into HDR scene, not swapchain
    depthFormat: formats.depth,
    cameraBindGroupLayout,
    dayNightBindGroupLayout: daynight.bindGroupLayout,
  });

  const terrain: TerrainPass = createTerrainPass(device, {
    presentationFormat: formats.hdrColor,
    depthFormat: formats.depth,
    heightmapView: gen.heightmap.view,
    biomeView: gen.biome.view,
    roadMaskView: gen.roads.maskView,
    cameraBindGroupLayout,
    dayNightBindGroupLayout: daynight.bindGroupLayout,
    shadowBindGroupLayout: shadow.bindGroupLayout,
    geometry: terrainGeometry,
  });

  const buildings: BuildingsPass = createBuildingsPass(device, {
    presentationFormat: formats.hdrColor,
    depthFormat: formats.depth,
    instanceBuffer: gen.buildings.instanceBuffer,
    instanceCountBuffer: gen.buildings.countBuffer,
    maxInstances: gen.buildings.maxInstances,
    cameraBindGroupLayout,
    dayNightBindGroupLayout: daynight.bindGroupLayout,
    shadowBindGroupLayout: shadow.bindGroupLayout,
  });

  const trees: TreesPass = createTreesPass(device, {
    presentationFormat: formats.hdrColor,
    depthFormat: formats.depth,
    instanceBuffer: gen.trees.instanceBuffer,
    instanceCountBuffer: gen.trees.countBuffer,
    maxInstances: gen.trees.maxInstances,
    cameraBindGroupLayout,
    dayNightBindGroupLayout: daynight.bindGroupLayout,
    shadowBindGroupLayout: shadow.bindGroupLayout,
  });

  const carsPass: CarsPass = createCarsPass(device, {
    presentationFormat: formats.hdrColor,
    depthFormat: formats.depth,
    instanceBuffer: cars.instanceBuffer,
    instanceCount: cars.maxInstances,
    cameraBindGroupLayout,
    dayNightBindGroupLayout: daynight.bindGroupLayout,
    shadowBindGroupLayout: shadow.bindGroupLayout,
  });

  const post: PostPass = createPostPass(device, {
    swapchainFormat: presentationFormat,
    hdrFormat: formats.hdrColor,
    dayNightBindGroupLayout: daynight.bindGroupLayout,
    cameraBindGroupLayout,
    initialHdrView: hdrView!,
  });

  const cameraScratch = new Float32Array(CAMERA_UBO_SIZE / 4);

  const writeCamera = (viewProj: Mat4, eye: Vec3, timeSec: number): void => {
    cameraScratch.set(viewProj, 0);
    cameraScratch[16] = eye[0];
    cameraScratch[17] = eye[1];
    cameraScratch[18] = eye[2];
    cameraScratch[19] = 1;
    cameraScratch[20] = gpu.canvas.width;
    cameraScratch[21] = gpu.canvas.height;
    cameraScratch[22] = gpu.canvas.width / gpu.canvas.height;
    cameraScratch[23] = timeSec;
    cameraScratch.set(mat4Invert(viewProj), 24);
    writeUniform(device, cameraBuffer, cameraScratch);
  };

  const render: FrameRenderer['render'] = (camera, timeSec) => {
    writeCamera(camera.viewProj, camera.eye, timeSec);
    daynight.update(timeSec);

    const encoder = device.createCommandEncoder({ label: 'frame' });

    // Shadow pass — depth-only from sun POV.
    shadow.draw(encoder, daynight.sunDir());

    // Main pass — sky/terrain/buildings into the HDR scene.
    const main = encoder.beginRenderPass({
      label: 'main',
      colorAttachments: [{
        view: hdrView!,
        loadOp: 'clear',
        storeOp: 'store',
        clearValue: { r: 0, g: 0, b: 0, a: 1 },
      }],
      depthStencilAttachment: {
        view: depthView!,
        depthClearValue: 1.0,
        depthLoadOp: 'clear',
        depthStoreOp: 'store',
      },
    });
    main.setBindGroup(0, cameraBindGroup);
    main.setBindGroup(2, daynight.bindGroup);
    main.setBindGroup(3, shadow.bindGroup);
    sky.draw(main);
    terrain.draw(main);
    buildings.draw(main);
    trees.draw(main);
    carsPass.draw(main);
    main.end();

    // Post — tonemap + bloom + vignette to swapchain.
    const swapchainView = context.getCurrentTexture().createView();
    const postPass = encoder.beginRenderPass({
      label: 'post',
      colorAttachments: [{
        view: swapchainView,
        loadOp: 'clear',
        storeOp: 'store',
        clearValue: { r: 0, g: 0, b: 0, a: 1 },
      }],
    });
    postPass.setBindGroup(0, cameraBindGroup);
    postPass.setBindGroup(2, daynight.bindGroup);
    post.draw(postPass);
    postPass.end();

    device.queue.submit([encoder.finish()]);
  };

  return {
    render,
    resize: () => {
      recreateTargets();
      // Post needs the new HDR view — rebind on resize.
      post.onResize(hdrView!);
    },
    daynight,
    destroy() {
      depthTexture?.destroy();
      hdrTexture?.destroy();
      sky.destroy();
      terrain.destroy();
      buildings.destroy();
      trees.destroy();
      carsPass.destroy();
      shadow.destroy();
      post.destroy();
      daynight.destroy();
      terrainGeometry.destroy();
    },
  };
}
